import { api } from '@xtnd/web/shared/api';

export type JuniperEmailStatusBucket = 'delivered' | 'partial' | 'failed' | 'pending';
export type JuniperEmailSendType =
  | 'initial_send'
  | 'release'
  | 'resend'
  | 'follow_up'
  | 'trr_release'
  | 'trr_follow_up'
  | 'unknown';

export interface JuniperEmailReportParams {
  dateFrom?: string;
  dateTo?: string;
  client?: string;
  matterId?: string;
  contactEmail?: string;
  juniperId?: string;
  matterType?: string;
  sendType?: string;
  statusBucket?: string;
  page?: number;
  limit?: number;
}

export interface JuniperEmailMatterOption {
  matterId: string;
  matterDisplayNumber?: string;
  clientName?: string;
}

export interface JuniperEmailFilterOptionsResponse {
  clients: string[];
  matters: JuniperEmailMatterOption[];
  recipients: string[];
  sendTypes: JuniperEmailSendType[];
  matterTypes: string[];
  statusBuckets: JuniperEmailStatusBucket[];
}

export interface JuniperEmailRecipientStatus {
  recipient: string;
  status: 'delivered' | 'failed' | 'pending';
  eventType: string;
  failureCategory?: string;
  failureReason?: string;
  diagnosticCode?: string;
  providerStatus?: string;
  bounceType?: string;
  bounceSubType?: string;
  complaintType?: string;
  complaintSubType?: string;
  lastEventTimestamp?: string;
}

export interface JuniperEmailRelatedEvent {
  timestamp: string;
  type: string;
  status: string;
  message?: string;
}

export interface JuniperEmailAttemptRow {
  attemptId: string;
  emailId: string;
  sentAt: string;
  juniperId: string;
  clientName?: string;
  matterId?: string;
  matterDisplayNumber?: string;
  matterType?: string;
  sendType: JuniperEmailSendType;
  toRecipients: string[];
  ccRecipients: string[];
  bccRecipients: string[];
  recipientCount: number;
  deliveredRecipientCount: number;
  failedRecipientCount: number;
  pendingRecipientCount: number;
  statusBucket: JuniperEmailStatusBucket;
  failureCategories: string[];
  latestSesEventTimestamp?: string;
  attachmentSummary?: string;
  triggeredBy?: string;
  subject?: string;
  recipientStatuses: JuniperEmailRecipientStatus[];
  relatedEvents?: JuniperEmailRelatedEvent[];
}

export interface JuniperEmailIssuesResponse {
  summary: {
    distinctBadAddresses: number;
    missingRecipientSetups: number;
    impactedClients: number;
    impactedMatters: number;
  };
  badAddresses: Array<{
    emailAddress: string;
    currentHealth: 'healthy' | 'watch' | 'bad' | 'suppressed';
    recommendedAction: string;
    primaryFailureCategory?: string;
    lastFailureTimestamp?: string;
    lastSuccessTimestamp?: string;
    affectedClientsCount: number;
    affectedMattersCount: number;
    failedAttemptsCount: number;
    successfulAttemptsCount: number;
    recentExampleMatter?: string;
  }>;
  missingRecipients: Array<{
    clientName?: string;
    matterId?: string;
    matterDisplayNumber?: string;
    juniperId: string;
    matterType?: string;
    firstSeen: string;
    lastSeen: string;
    currentJuniperStatus: string;
    recommendedAction: string;
  }>;
  failureCategoryTotals: Array<{ category: string; count: number }>;
}

export interface JuniperEmailOverviewResponse {
  kpis: {
    totalAttempts: number;
    uniqueMatters: number;
    uniqueRecipients: number;
    deliveredAttempts: number;
    partialAttempts: number;
    failedAttempts: number;
    pendingAttempts: number;
    distinctBadAddresses: number;
    complaintCount: number;
    hardBounceCount: number;
  };
  rates: {
    deliveryRate: number;
    partialRate: number;
    failureRate: number;
    complaintRate: number;
    badAddressRate: number;
  };
  trendSeries: Array<{
    label: string;
    delivered: number;
    partial: number;
    failed: number;
    pending: number;
  }>;
  failureCategoryTotals: Array<{ category: string; count: number }>;
  periodSeries: Array<{
    label: string;
    delivered: number;
    partial: number;
    failed: number;
    pending: number;
  }>;
  cadenceHeatmap: Array<{ month: string; counts: number[] }>;
  cadenceInsights: Array<{
    clientName?: string;
    cadenceType: 'monthly' | 'semi_monthly' | 'weekly' | 'irregular';
    anchorWindows: string[];
    confidence: 'high' | 'medium' | 'low';
    monthsObserved: number;
    dominantWindowCoveragePct: number;
    totalAttempts: number;
  }>;
}

export interface JuniperEmailHistoryResponse {
  data: JuniperEmailAttemptRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    returned: number;
  };
}

class JuniperEmailReportApiService {
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });

    return searchParams.toString();
  }

  async getFilterOptions(params: JuniperEmailReportParams = {}) {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    return api.get<JuniperEmailFilterOptionsResponse>(
      `/reports/juniper-email/filter-options${queryString ? `?${queryString}` : ''}`
    );
  }

  async getOverview(params: JuniperEmailReportParams = {}) {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    return api.get<JuniperEmailOverviewResponse>(
      `/reports/juniper-email/overview${queryString ? `?${queryString}` : ''}`
    );
  }

  async getIssues(params: JuniperEmailReportParams = {}) {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    return api.get<JuniperEmailIssuesResponse>(
      `/reports/juniper-email/issues${queryString ? `?${queryString}` : ''}`
    );
  }

  async getHistory(params: JuniperEmailReportParams = {}) {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    return api.get<JuniperEmailHistoryResponse>(
      `/reports/juniper-email/history${queryString ? `?${queryString}` : ''}`
    );
  }

  async getAttemptDetail(attemptId: string, params: JuniperEmailReportParams = {}) {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    return api.get<JuniperEmailAttemptRow>(
      `/reports/juniper-email/history/${attemptId}${queryString ? `?${queryString}` : ''}`
    );
  }
}

export const juniperEmailReportApi = new JuniperEmailReportApiService();
